import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';
import { take } from 'lodash';

import FilterGroup from 'components/search_sidebar/filter_group';
import Group from './group';

import 'components/styles/filters.scss';

export const MAX_GROUPS = 10;

export default class GroupFilter extends React.Component {
  static propTypes = {
    allMembersGroupLabel: PropTypes.string.isRequired,
    groupByDatabase: PropTypes.bool,
    groups: PropTypes.arrayOf(PropTypes.shape({
      companyCount: PropTypes.number,
      count: PropTypes.number,
      databaseId: PropTypes.number.isRequired,
      databaseName: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string.isRequired,
      personCount: PropTypes.number
    })).isRequired,
    memberCountProperty: PropTypes.string.isRequired,
    selectedGroupIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
    showLabel: PropTypes.bool,
    totalCount: PropTypes.number.isRequired,
    totalUngroupedCount: PropTypes.number.isRequired,
    onGroupDeselect: PropTypes.func.isRequired,
    onGroupSelect: PropTypes.func.isRequired
  }

  static defaultProps = {
    memberCountProperty: 'count',
    groupByDatabase: false,
    showLabel: true
  }

  state = {
    displayAllGroups: false
  }

  get allMembersGroup() {
    return {
      id: null,
      name: this.props.allMembersGroupLabel
    };
  }

  get ungroupedMembersGroup() {
    return {
      id: 'ungrouped',
      name: '(No Group)'
    };
  }

  get databases() {
    const groupsByDatabaseId = {};
    for (const group of this.props.groups) {
      const { databaseId, databaseName } = group;
      groupsByDatabaseId[databaseId] = groupsByDatabaseId[databaseId] || {
        id: databaseId,
        name: databaseName,
        groups: []
      };

      groupsByDatabaseId[databaseId].groups.push(group);
    }

    return Object.values(groupsByDatabaseId);
  }

  get label() {
    return this.props.showLabel ? 'Groups' : null;
  }

  displayAllGroups = () => {
    this.setState({ displayAllGroups: true });
  }

  displaySomeGroups = () => {
    this.setState({ displayAllGroups: false });
  }

  handleGroupSelect = (group) => {
    this.props.onGroupSelect(group);
  }

  handleGroupDeselect = (group) => {
    this.props.onGroupDeselect(group);
  }

  render() {
    const { groups } = this.props;
    if (groups.length === 0) {
      return null;
    }

    const max = this.state.displayAllGroups ? groups.length : Math.min(groups.length, MAX_GROUPS);

    return (
      <FilterGroup label={this.label}>
        {this.renderGroup(this.allMembersGroup, this.props.totalCount)}
        {
          this.props.groupByDatabase
            ? this.renderGroupsByDatabase(max)
            : this.renderGroups(take(this.props.groups, max))
        }
        {this.renderGroup(this.ungroupedMembersGroup, this.props.totalUngroupedCount)}
        {this.renderDisplayConrols()}
      </FilterGroup>
    );
  }

  renderGroupsByDatabase(count) {
    let rendered = 0;

    return this.databases.map((database) => {
      if (rendered < count) {
        const groupPage = take(database.groups, count - rendered);
        rendered += groupPage.length;

        return (
          <div key={`database-${database.id}`}>
            <div className="h5 text-muted">{database.name}</div>
            {this.renderGroups(groupPage)}
          </div>
        );
      }
    });
  }

  renderGroups(groups) {
    return groups.map((group, i) => (
      <div key={i}>
        {this.renderGroup(group, group[this.props.memberCountProperty])}
      </div>
    ));
  }

  renderGroup(group, memberCount) {
    return (
      <Group
        group={{ ...group, memberCount }}
        selected={group.id ? this.props.selectedGroupIds.includes(group.id) : !this.props.selectedGroupIds.length}
        onDeselect={this.handleGroupDeselect}
        onSelect={this.handleGroupSelect}
      />
    );
  }

  renderDisplayConrols() {
    if (this.props.groups.length <= MAX_GROUPS) {
      return null;
    }

    let icon, label, clickHandler;
    if (this.state.displayAllGroups) {
      icon = faAngleUp;
      label = 'Show Less';
      clickHandler = this.displaySomeGroups;
    } else {
      icon = faAngleDown;
      label = 'Show More';
      clickHandler = this.displayAllGroups;
    }

    return (
      <div className="clickable mb-2 group-text text-xs" name="toggle-all" onClick={clickHandler}>
        <span>
          {label}
          <FontAwesomeIcon className="ml-2" icon={icon} />
        </span>
      </div>
    );
  }
}
